import { marked } from 'marked';
import DOMPurify from 'dompurify';

import CodeMirror, { LANG_MODE } from 'app/utils/codemirror/codemirrorSetup';

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
const escapeHtml = s => s.replace(/[&<>"']/g, c => HTML_ESCAPES[c]);

const tokenSpan = (text, style) => {
    const escaped = escapeHtml(text);
    if (!style) return escaped;
    const cls = style.split(' ').map(s => `cm-${s}`).join(' ');
    return `<span class="${cls}">${escaped}</span>`;
};

const highlightCode = (code, lang) => {
    const mode = LANG_MODE[lang] || 'javascript';
    try {
        let html = '';
        CodeMirror.runMode(code, mode, (text, style) => { html += tokenSpan(text, style); });
        return html;
    } catch (e) {
        return escapeHtml(code);
    }
};

const renderer = new marked.Renderer();
renderer.link = (href, title, text) => `<a target="_blank" href="${href || '#'}" title="${title || ''}">${text}</a>`;
renderer.code = (code, info) => {
    const lang = (info || '').trim().split(/\s+/)[0].toLowerCase();
    return `<pre><code class="cm-code">${highlightCode(code, lang)}</code></pre>`;
};

marked.use({ renderer: renderer });

export const escapeEmptyMarkdownWrappers = source => (source || '')
    .replace(/(?<!\*)\*(?!\*)(\S(?:[^*\n]*?\S)?)\*(?!\*)/g, '**$1**')
    .replace(/(?<!~)~(?!~)(\S(?:[^~\n]*?\S)?)~(?!~)/g, '~~$1~~')
    .replace(/```[^\S\n]*\n[^\S\n]*\n[^\S\n]*```/g, '\\`\\`\\`\n\n\\`\\`\\`')
    .replace(/\*\*\*\*/g, '\\*\\*\\*\\*')
    .replace(/~~~~/g, '\\~\\~\\~\\~')
    .replace(/(?<!_)__(?!_)/g, '\\_\\_')
    .replace(/(?<!`)``(?!`)/g, '\\`\\`');

const markdownToHtml = source => {
    if (!source || typeof source !== 'string') {
        return null;
    }
    try {
        const markdown = escapeEmptyMarkdownWrappers(source)
            .replaceAll(/\[ \]\s/gm, '<i class="mdi mdi-checkbox-blank-outline checkbox"></i> ')
            .replaceAll(/\[x\]\s/gm, '<i class="mdi mdi-checkbox-marked checkbox"></i> ');
        const html = marked.parse(markdown);
        const sanetized = DOMPurify.sanitize(html, {
            ADD_ATTR: ['style'],
            ADD_TAGS: ['style']
        }).replace(/<a /g, '<a target="_blank" ');
        return { __html: sanetized };
    } catch (error) {}
    return null;
};

export { markdownToHtml, renderer };

export const MARKDOWN_TOKENS = {
    bold: '*',
    italic: '_',
    strikethrough: '~',
    code: '`',
    codeBlock: { open: '```\n', close: '\n```' },
};

export const wrapInputSelection = (inputEl, value, token) => {
    const base = value || '';
    const open = typeof token === 'string' ? token : token.open;
    const close = typeof token === 'string' ? token : token.close;
    if (inputEl && typeof inputEl.selectionStart === 'number') {
        const start = inputEl.selectionStart;
        const end = inputEl.selectionEnd;
        const selected = base.slice(start, end);
        const innerStart = start + open.length;
        const innerEnd = end + open.length;
        return {
            value: `${base.slice(0, start)}${open}${selected}${close}${base.slice(end)}`,
            cursorPos: selected ? innerEnd + close.length : innerStart,
            selectionStart: innerStart,
            selectionEnd: innerEnd,
        };
    }
    const insertPos = base.length + open.length;
    return {
        value: `${base}${open}${close}`,
        cursorPos: insertPos,
        selectionStart: insertPos,
        selectionEnd: insertPos,
    };
};

export const restoreInputCursor = (inputEl, start, end) => {
    if (!inputEl) return;
    const endPos = end === undefined ? start : end;
    setTimeout(() => {
        if (inputEl.isConnected === false) return;
        inputEl.focus();
        try {
            inputEl.setSelectionRange(start, endPos);
        } catch (e) {
            // setSelectionRange unsupported on some input types — ignore
        }
    }, 0);
};

export default marked;
